(function() {
  'use strict';
  
  // ==================== CONFIGURATION ====================
  const CONFIG = {
    chatbase: {
      apiKey: '6463a3b4-bb42-4ca5-b086-69a5c8d269b2',
      chatbotId: '49DGM0FSWoXQ3Xs-cGAPo',
      apiUrl: 'https://www.chatbase.co/api/v1/chat',
      timeout: 15000 // 15 second timeout for better performance
    },
    shopify: {
      storefrontToken: 'db2f34251c436c16b1ac23b7998a03ab',
      storeDomain: 'bytehaus.myshopify.com',
      apiVersion: '2024-01'
    },
    storage: {
      conversationKey: 'chatbase_conversation',
      maxHistoryItems: 50 // Limit history for performance
    },
    ui: {
      mobileBreakpoint: 768,
      animationDuration: 300,
      debounceDelay: 200,
      typingDelay: 300
    },
    debug: {
      enabled: false, // Enable debugging
      logLevel: 'verbose' // 'minimal', 'normal', 'verbose'
    }
  };

  // ==================== DEBUGGING UTILITIES ====================
  const Debug = {
    log(level, ...args) {
      if (!CONFIG.debug.enabled) return;
      
      const levels = { minimal: 1, normal: 2, verbose: 3 };
      const currentLevel = levels[CONFIG.debug.logLevel] || 2;
      const messageLevel = levels[level] || 2;
      
      if (messageLevel <= currentLevel) {
        console.log(`[HybridSearch:${level.toUpperCase()}]`, ...args);
      }
    },

    async validateConfig() {
      this.log('normal', '🔍 Validating configuration...');
      
      const issues = [];
      
      if (!CONFIG.chatbase.apiKey || CONFIG.chatbase.apiKey.trim() === '') {
        issues.push('❌ API key is missing or empty');
      } else if (CONFIG.chatbase.apiKey.length < 20) {
        issues.push('⚠️ API key seems too short (might be invalid)');
      } else {
        this.log('verbose', '✅ API key is present and reasonable length');
      }
      
      if (!CONFIG.chatbase.chatbotId || CONFIG.chatbase.chatbotId.trim() === '') {
        issues.push('❌ Chatbot ID is missing or empty');
      } else {
        this.log('verbose', '✅ Chatbot ID is present:', CONFIG.chatbase.chatbotId);
      }
      
      if (!CONFIG.chatbase.apiUrl || !CONFIG.chatbase.apiUrl.startsWith('http')) {
        issues.push('❌ API URL is missing or invalid');
      } else {
        this.log('verbose', '✅ API URL is valid:', CONFIG.chatbase.apiUrl);
      }
      
      if (issues.length > 0) {
        this.log('minimal', '🚨 Configuration issues found:');
        issues.forEach(issue => this.log('minimal', issue));
        return false;
      } else {
        this.log('normal', '✅ Configuration looks good!');
        return true;
      }
    },

    async checkCORS() {
      this.log('normal', '🌐 Checking CORS...');
      
      try {
        const response = await fetch(CONFIG.chatbase.apiUrl, {
          method: 'OPTIONS',
          headers: {
            'Origin': window.location.origin,
            'Access-Control-Request-Method': 'POST',
            'Access-Control-Request-Headers': 'Content-Type, Authorization'
          }
        });
        
        this.log('verbose', '🌐 CORS preflight status:', response.status);
        this.log('verbose', '🌐 CORS headers:', Object.fromEntries(response.headers.entries()));
        
        const allowOrigin = response.headers.get('Access-Control-Allow-Origin');
        const allowMethods = response.headers.get('Access-Control-Allow-Methods');
        const allowHeaders = response.headers.get('Access-Control-Allow-Headers');
        
        this.log('verbose', '🌐 Allow-Origin:', allowOrigin);
        this.log('verbose', '🌐 Allow-Methods:', allowMethods);
        this.log('verbose', '🌐 Allow-Headers:', allowHeaders);
        
        if (allowOrigin === '*' || allowOrigin === window.location.origin) {
          this.log('normal', '✅ CORS looks good');
          return true;
        } else {
          this.log('normal', '❌ CORS might be blocking requests');
          return false;
        }
        
      } catch (error) {
        this.log('normal', '❌ CORS check failed:', error);
        return false;
      }
    },

    async testChatbaseAPI() {
      this.log('normal', '🧪 Testing Chatbase API connectivity...');
      
      try {
        const testPayload = {
          chatbotId: CONFIG.chatbase.chatbotId,
          messages: [{ role: 'user', content: 'Hello' }]
        };
        
        this.log('verbose', '🔬 Test payload:', testPayload);
        
        const response = await fetch(CONFIG.chatbase.apiUrl, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${CONFIG.chatbase.apiKey}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(testPayload)
        });
        
        this.log('verbose', '🔬 Test response status:', response.status);
        this.log('verbose', '🔬 Test response headers:', Object.fromEntries(response.headers.entries()));
        
        const responseText = await response.text();
        this.log('verbose', '🔬 Test response body:', responseText);
        
        if (response.ok) {
          this.log('normal', '✅ API test successful!');
          return true;
        } else {
          this.log('normal', '❌ API test failed');
          return false;
        }
        
      } catch (error) {
        this.log('normal', '❌ API test error:', error);
        return false;
      }
    }
  };

  // Make debug functions globally available
  window.HybridSearchDebug = Debug;

  // ==================== STATE MANAGEMENT ====================
  class HybridSearchState {
    constructor() {
      this.isAdjusting = false;
      this.resizeTimeout = null;
      this.cores = new Map();
      this.conversationHistory = [];
      this.actualViewportHeight = window.innerHeight;
      this.isProblematicWebview = this.detectProblematicWebview();
    }

    detectProblematicWebview() {
      const userAgent = navigator.userAgent;
      const isLine = userAgent.includes('Line');
      const isMessenger = userAgent.includes('FBAN') || userAgent.includes('FBAV');
      const isWeChat = userAgent.includes('MicroMessenger');
      const isInApp = userAgent.includes('Instagram') || userAgent.includes('Twitter') || userAgent.includes('LinkedIn');
      
      return isLine || isMessenger || isWeChat || isInApp;
    }

    getViewportConstraints() {
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;
      const isMobile = viewportWidth <= CONFIG.ui.mobileBreakpoint;
      
      const actualHeight = isMobile ? viewportHeight : window.innerHeight;
      
      return {
        viewportHeight: actualHeight,
        viewportWidth,
        isMobile,
        maxContainerHeight: isMobile ? 
          Math.min(actualHeight * 0.9, actualHeight - 100) : 
          Math.min(actualHeight * 0.8, 660),
        maxPopoverHeight: actualHeight * (isMobile ? 0.95 : 0.85),
        maxDrawerHeight: actualHeight * 0.95,
        headerReservedSpace: isMobile ? 120 : 100
      };
    }

    updateViewportHeight() {
      const newHeight = window.innerHeight;
      if (Math.abs(newHeight - this.actualViewportHeight) > 10) {
        this.actualViewportHeight = newHeight;
        this.updateCSSViewportHeight();
      }
    }

    updateCSSViewportHeight() {
      document.documentElement.style.setProperty('--actual-vh', `${this.actualViewportHeight}px`);
      document.documentElement.style.setProperty('--actual-vh-percent', `${this.actualViewportHeight / 100}px`);
    }

    shouldMaintainMobileExpansion(core) {
      return core.hasAttribute('data-mobile-expansion-locked');
    }

    lockMobileExpansion(core) {
      core.setAttribute('data-mobile-expansion-locked', 'true');
    }

    hasExpandableContent(core) {
      const hasUserMessages = core.querySelector('#chatMessages .user-message');
      const chatMessagesCount = core.querySelectorAll('#chatMessages .message').length;
      const hasActualMessages = hasUserMessages || chatMessagesCount > 1;

      const predictiveSearch = core.querySelector('.predictive-search');
      const hasSearchResults = predictiveSearch && this.hasRealSearchResults(predictiveSearch);

      return hasActualMessages || hasSearchResults;
    }

    hasRealSearchResults(predictiveSearch) {
      const isVisible = predictiveSearch.offsetHeight > 0 && 
                       predictiveSearch.style.display !== 'none';
      
      if (!isVisible) return false;

      const hasResultItems = predictiveSearch.querySelector('.predictive-search__item') ||
                            predictiveSearch.querySelector('.predictive-search__result') ||
                            predictiveSearch.querySelector('[id*="predictive-search-option"]:not([aria-hidden="true"])');
      
      const hasResultsWithContent = predictiveSearch.querySelector('#predictive-search-results-groups-wrapper') &&
                                   predictiveSearch.querySelector('#predictive-search-results-groups-wrapper').children.length > 0;

      return hasResultItems || hasResultsWithContent;
    }
  }

  const state = new HybridSearchState();

  // ==================== UTILITIES ====================
  const Utils = {
    debounce(func, wait) {
      let timeout;
      return function executedFunction(...args) {
        const later = () => {
          clearTimeout(timeout);
          func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
      };
    },

    throttle(func, limit) {
      let inThrottle;
      return function(...args) {
        if (!inThrottle) {
          func.apply(this, args);
          inThrottle = true;
          setTimeout(() => inThrottle = false, limit);
        }
      };
    },

    addTransition(element, properties = null) {
      if (!element || element.hasAttribute('data-transition-added')) return;
      
      const constraints = state.getViewportConstraints();
      const defaultTransition = constraints.isMobile 
        ? 'height 0.4s cubic-bezier(0.4, 0, 0.2, 1), max-height 0.4s cubic-bezier(0.4, 0, 0.2, 1)'
        : 'height 0.3s ease, max-height 0.3s ease';
      
      element.style.transition = properties || defaultTransition;
      element.setAttribute('data-transition-added', 'true');
    },

    removeTransition(element) {
      if (element) {
        element.style.transition = '';
        element.removeAttribute('data-transition-added');
      }
    },

    addMobileTransitions(element) {
      if (!element) return;
      element.style.transition = 'height 0.4s cubic-bezier(0.4, 0, 0.2, 1), max-height 0.4s cubic-bezier(0.4, 0, 0.2, 1), flex 0.4s cubic-bezier(0.4, 0, 0.2, 1)';
    }
  };

  // ==================== PRODUCT SEARCH DISABLER ====================
  class ProductSearchDisabler {
    constructor(core) {
      this.core = core;
      this.disableProductSearch();
    }

    disableProductSearch() {
      Debug.log('normal', '🚫 Disabling product search submission...');
      
      // Find the product search input
      const searchInput = this.core.querySelector('#Search-In-Hybrid-Products');
      if (searchInput) {
        this.disableSearchInput(searchInput);
      }
      
      // Find and disable any search buttons
      this.disableSearchButtons();
      
      // Find and disable form submission
      this.disableFormSubmission();
      
      Debug.log('normal', '✅ Product search submission disabled');
    }

    disableSearchInput(searchInput) {
      Debug.log('verbose', '🔧 Disabling search input Enter key...');
      
      // Prevent Enter key submission
      searchInput.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.keyCode === 13) {
          e.preventDefault();
          e.stopPropagation();
          Debug.log('verbose', '🚫 Prevented Enter key submission on product search');
          return false;
        }
      }, true);

      // Also prevent keypress for extra safety
      searchInput.addEventListener('keypress', (e) => {
        if (e.key === 'Enter' || e.keyCode === 13) {
          e.preventDefault();
          e.stopPropagation();
          Debug.log('verbose', '🚫 Prevented Enter keypress on product search');
          return false;
        }
      }, true);
    }

    disableSearchButtons() {
      Debug.log('verbose', '🔧 Disabling search buttons...');
      
      // Common search button selectors
      const buttonSelectors = [
        'button[type="submit"]',
        '.search-button',
        '.predictive-search__search-button',
        '[data-search-button]',
        'button[aria-label*="search" i]',
        'button[title*="search" i]',
        '.search__button',
        '.btn-search',
        'input[type="submit"]'
      ];
      
      buttonSelectors.forEach(selector => {
        const buttons = this.core.querySelectorAll(selector);
        buttons.forEach(button => {
          // Check if it's within the products content area
          const productsContent = button.closest('.products-content');
          if (productsContent) {
            this.disableButton(button);
          }
        });
      });
    }

    disableButton(button) {
      Debug.log('verbose', '🚫 Disabling button:', button);
      
      // Prevent click events
      button.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        Debug.log('verbose', '🚫 Prevented button click on product search');
        return false;
      }, true);
      
      // Prevent form submission on button
      button.addEventListener('submit', (e) => {
        e.preventDefault();
        e.stopPropagation();
        Debug.log('verbose', '🚫 Prevented form submission from button');
        return false;
      }, true);
      
      // Disable the button element
      button.disabled = true;
      button.setAttribute('disabled', 'true');
      
      // Add visual indication that it's disabled
      button.style.opacity = '0.6';
      button.style.pointerEvents = 'none';
      button.title = 'Product search is disabled';
    }

    disableFormSubmission() {
      Debug.log('verbose', '🔧 Disabling form submission...');
      
      // Find forms that contain the product search
      const forms = this.core.querySelectorAll('form');
      forms.forEach(form => {
        const hasProductSearch = form.querySelector('#Search-In-Hybrid-Products');
        if (hasProductSearch) {
          this.disableForm(form);
        }
      });
      
      // Also look for forms that might be outside but related
      const searchInput = this.core.querySelector('#Search-In-Hybrid-Products');
      if (searchInput) {
        const parentForm = searchInput.closest('form');
        if (parentForm) {
          this.disableForm(parentForm);
        }
      }
    }

    disableForm(form) {
      Debug.log('verbose', '🚫 Disabling form:', form);
      
      // Prevent form submission
      form.addEventListener('submit', (e) => {
        e.preventDefault();
        e.stopPropagation();
        Debug.log('verbose', '🚫 Prevented form submission');
        return false;
      }, true);
      
      // Override form action
      form.setAttribute('action', 'javascript:void(0)');
      
      // Add data attribute to mark as disabled
      form.setAttribute('data-search-disabled', 'true');
    }
  }

  // ==================== HEIGHT MANAGEMENT ====================
  class HeightManager {
    constructor(core) {
      this.core = core;
      this.constraints = state.getViewportConstraints();
    }

    updateConstraints() {
      this.constraints = state.getViewportConstraints();
    }

    resetAllHeights() {
      const { isMobile } = this.constraints;
      
      if (isMobile && state.shouldMaintainMobileExpansion(this.core)) {
        return;
      }

      const containers = this.getContainers();
      
      containers.forEach(container => {
        if (container) {
          container.style.height = '';
          container.style.maxHeight = '';
          container.style.overflow = '';
        }
      });

      this.core.style.height = '';
      this.core.style.maxHeight = '';
      this.core.style.overflow = '';

      if (isMobile) {
        this.resetMobileState();
      }

      this.resetProductSearchState();
    }

    resetMobileState() {
      if (state.shouldMaintainMobileExpansion(this.core)) {
        return;
      }

      const mobileDrawerContent = this.core.closest('.mobile-drawer-content');
      
      if (mobileDrawerContent) {
        mobileDrawerContent.style.height = '';
        mobileDrawerContent.style.maxHeight = '';
        mobileDrawerContent.classList.remove('mobile-expanded');
      }

      this.core.classList.remove('mobile-expanded');
      this.core.removeAttribute('data-mobile-expanded');
      
      const allTabContents = this.core.querySelectorAll('.tab-content');
      allTabContents.forEach(content => {
        content.classList.remove('mobile-expanded-content');
        this.resetElementStyles(content);
        
        const chatContainer = content.querySelector('.chat-container');
        const chatMessages = content.querySelector('#chatMessages');
        
        if (chatContainer) this.resetElementStyles(chatContainer);
        if (chatMessages) this.resetElementStyles(chatMessages);
      });

      this.resetElementStyles(this.core, ['height', 'display', 'flexDirection']);
    }

    resetProductSearchState() {
      const productsContent = this.core.querySelector('.products-content');
      if (productsContent) {
        productsContent.classList.remove('has-results');
        
        const searchField = productsContent.querySelector('.field');
        if (searchField) searchField.classList.remove('sticky-search');
      }

      const predictiveSearch = this.core.querySelector('.predictive-search');
      if (predictiveSearch) {
        const resultTabs = predictiveSearch.querySelector('.predictive-search__result-tabs');
        if (resultTabs) resultTabs.classList.remove('sticky-tabs');
      }
    }

    resetElementStyles(element, properties = ['height', 'flex', 'display', 'flexDirection', 'minHeight', 'overflowY']) {
      if (!element) return;
      properties.forEach(prop => {
        element.style[prop] = '';
      });
    }

    getContainers() {
      return [
        this.core.closest('#chatbase-popover'),
        this.core.closest('.mobile-drawer'),
        this.core.closest('.mobile-drawer-content')
      ].filter(Boolean);
    }

    expandForMobile() {
      const { isMobile } = this.constraints;
      if (!isMobile) return;

      if (this.core.hasAttribute('data-mobile-expanded') && state.shouldMaintainMobileExpansion(this.core)) {
        this.configureActiveTabForMobile();
        return;
      }

      if (!state.hasExpandableContent(this.core)) {
        return;
      }

      const mobileDrawerContent = this.core.closest('.mobile-drawer-content');
      if (!mobileDrawerContent) return;
      
      const containers = this.getContainers();
      containers.forEach(container => {
        if (container) {
          Utils.addTransition(container);
        }
      });
      
      Utils.addMobileTransitions(this.core);
      Utils.addMobileTransitions(mobileDrawerContent);
      
      if (state.isProblematicWebview) {
        mobileDrawerContent.classList.add('position-fix');
        mobileDrawerContent.style.position = 'fixed';
        mobileDrawerContent.style.top = '0';
        mobileDrawerContent.style.left = '0';
        mobileDrawerContent.style.right = '0';
        mobileDrawerContent.style.bottom = '0';
        mobileDrawerContent.style.height = 'auto';
        mobileDrawerContent.style.maxHeight = 'none';
      } else {
        const actualHeight = window.innerHeight;
        
        mobileDrawerContent.style.height = `${actualHeight}px`;
        mobileDrawerContent.style.maxHeight = `${actualHeight}px`;
        
        state.actualViewportHeight = actualHeight;
        document.documentElement.style.setProperty('--actual-vh', `${actualHeight}px`);
        
        if (CSS.supports('height', '100svh')) {
          setTimeout(() => {
            const testHeight = mobileDrawerContent.offsetHeight;
            if (testHeight > 0 && Math.abs(testHeight - actualHeight) < 50) {
              mobileDrawerContent.style.height = '100svh';
              mobileDrawerContent.style.maxHeight = '100svh';
            }
          }, 100);
        } else if (CSS.supports('height', '-webkit-fill-available')) {
          mobileDrawerContent.style.height = '-webkit-fill-available';
          mobileDrawerContent.style.maxHeight = '-webkit-fill-available';
        }
      }
      
      mobileDrawerContent.classList.add('mobile-expanded');
      
      this.core.classList.add('mobile-expanded');
      this.core.style.height = '100%';
      this.core.style.display = 'flex';
      this.core.style.flexDirection = 'column';
      
      this.configureActiveTabForMobile();
      
      this.core.setAttribute('data-mobile-expanded', 'true');
      
      state.lockMobileExpansion(this.core);
    }

    configureActiveTabForMobile() {
      const activeTabContent = this.core.querySelector('.tab-content.active');
      if (!activeTabContent) return;

      Utils.addMobileTransitions(activeTabContent);

      activeTabContent.classList.add('mobile-expanded-content');
      activeTabContent.style.height = '100%';
      activeTabContent.style.flex = '1';
      activeTabContent.style.display = 'flex';
      activeTabContent.style.flexDirection = 'column';
      activeTabContent.style.minHeight = '0';
      
      if (activeTabContent.classList.contains('ai-content')) {
        this.configureChatForMobile(activeTabContent);
      }
    }

    configureChatForMobile(tabContent) {
      const chatContainer = tabContent.querySelector('.chat-container');
      const chatMessages = tabContent.querySelector('#chatMessages');
      
      if (chatContainer) {
        Utils.addMobileTransitions(chatContainer);
        chatContainer.style.height = '100%';
        chatContainer.style.display = 'flex';
        chatContainer.style.flexDirection = 'column';
      }
      
      if (chatMessages) {
        Utils.addMobileTransitions(chatMessages);
        chatMessages.style.flex = '1';
        chatMessages.style.overflowY = 'auto';
        chatMessages.style.minHeight = '0';
      }
    }

    expandChatWindow() {
      const { isMobile } = this.constraints;
      
      if (isMobile) {
        this.expandForMobile();
        return;
      }

      const activeTab = this.core.querySelector('.tab.active');
      const isAITabActive = activeTab?.getAttribute('data-tab') === 'ai';
      
      if (!isAITabActive) {
        return;
      }
      
      const containers = this.getContainers();
      containers.forEach(container => {
        if (container) {
          Utils.addTransition(container);
          if (container.id === 'chatbase-popover') {
            container.style.height = '660px';
            container.style.maxHeight = '660px';
            container.style.overflow = 'hidden';
          } else {
            container.style.height = 'auto';
            container.style.maxHeight = '90vh';
            container.style.overflow = 'hidden';
          }
        }
      });

      this.core.style.height = '660px';
      this.core.style.maxHeight = '660px';
      this.core.style.overflow = 'hidden';
      
      this.core.setAttribute('data-chat-expanded', 'true');
    }

    adjustForProductSearch() {
      if (state.isAdjusting) return;
      state.isAdjusting = true;

      try {
        this.updateConstraints();
        const { isMobile, maxContainerHeight, maxPopoverHeight, maxDrawerHeight } = this.constraints;
        
        const activeTab = this.core.querySelector('.tab.active');
        if (activeTab?.getAttribute('data-tab') !== 'products') {
          state.isAdjusting = false;
          return;
        }

        const predictiveSearch = this.core.querySelector('.predictive-search');
        const productsContent = this.core.querySelector('.products-content');
        
        if (!predictiveSearch || !productsContent) {
          state.isAdjusting = false;
          return;
        }

        const hasResults = this.checkForSearchResults(predictiveSearch);
        
        if (isMobile && hasResults) {
          if (state.hasRealSearchResults(predictiveSearch)) {
            this.expandForMobile();
          }
          state.isAdjusting = false;
          return;
        }

        if (!isMobile || !state.shouldMaintainMobileExpansion(this.core)) {
          const containers = this.getContainers();
          containers.forEach(container => Utils.addTransition(container));

          if (hasResults) {
            this.applyExpandedSearchStyles(containers, maxPopoverHeight, maxDrawerHeight, maxContainerHeight, productsContent, predictiveSearch);
          } else {
            this.applyCompactSearchStyles(containers, maxContainerHeight, productsContent, predictiveSearch);
          }
        }

      } catch (error) {
        Debug.log('normal', 'Error in adjustForProductSearch:', error);
      } finally {
        state.isAdjusting = false;
      }
    }

    checkForSearchResults(predictiveSearch) {
      return state.hasRealSearchResults(predictiveSearch);
    }

    applyExpandedSearchStyles(containers, maxPopoverHeight, maxDrawerHeight, maxContainerHeight, productsContent, predictiveSearch) {
      const [popover, drawer] = containers;
      
      if (popover) {
        popover.style.height = 'auto';
        popover.style.maxHeight = maxPopoverHeight + 'px';
      }
      if (drawer) {
        drawer.style.height = 'auto';
        drawer.style.maxHeight = maxDrawerHeight + 'px';
      }
      
      this.core.style.height = 'auto';
      this.core.style.maxHeight = maxContainerHeight + 'px';
      
      productsContent.classList.add('has-results');
      
      const searchField = productsContent.querySelector('.field');
      if (searchField) searchField.classList.add('sticky-search');
      
      const resultTabs = predictiveSearch.querySelector('.predictive-search__result-tabs');
      if (resultTabs) resultTabs.classList.add('sticky-tabs');
      
      ['#predictive-search-results-groups-wrapper', '.predictive-search__result-group'].forEach(selector => {
        const element = this.core.querySelector(selector);
        if (element) {
          element.style.removeProperty('--search-height');
          element.style.maxHeight = 'none';
          element.style.height = 'auto';
          element.style.overflow = 'visible';
        }
      });
    }

    applyCompactSearchStyles(containers, maxContainerHeight, productsContent, predictiveSearch) {
      const [popover, drawer] = containers;
      
      if (popover) {
        popover.style.height = '';
        popover.style.maxHeight = '';
      }
      if (drawer) {
        drawer.style.height = '';
        drawer.style.maxHeight = '';
      }
      
      this.core.style.height = '';
      this.core.style.maxHeight = maxContainerHeight + 'px';
      
      productsContent.classList.remove('has-results');
      
      const searchField = productsContent.querySelector('.field');
      if (searchField) searchField.classList.remove('sticky-search');
      
      const resultTabs = predictiveSearch.querySelector('.predictive-search__result-tabs');
      if (resultTabs) resultTabs.classList.remove('sticky-tabs');
    }
  }

  // ==================== CHAT INTERFACE ====================
  class ChatInterface {
    constructor(core) {
      this.core = core;
      this.heightManager = new HeightManager(core);
      this.elements = this.getElements();
      this.conversationHistory = [];
      this.requestController = null;
      
      if (this.elements.isValid) {
        this.loadConversation();
        this.bindEvents();
      }
    }

    getElements() {
      const elements = {
        chatInput: this.core.querySelector('#chatInput'),
        sendButton: this.core.querySelector('#sendButton'),
        clearButton: this.core.querySelector('#clearChatButton'),
        menuButton: this.core.querySelector('#chatMenuButton'),
        menuDropdown: this.core.querySelector('#chatMenuDropdown'),
        chatMessages: this.core.querySelector('#chatMessages')
      };
      
      elements.isValid = elements.chatInput && elements.sendButton && elements.chatMessages;
      return elements;
    }

    bindEvents() {
      const { chatInput, sendButton, clearButton, menuButton, menuDropdown } = this.elements;
      
      sendButton.addEventListener('click', () => this.sendMessage());
      chatInput.addEventListener('keypress', (e) => {
        if (e.key === 'Enter') this.sendMessage();
      });

      if (menuButton && menuDropdown) {
        menuButton.addEventListener('click', (e) => {
          e.stopPropagation();
          menuDropdown.classList.toggle('show');
        });
        
        document.addEventListener('click', (e) => {
          if (!menuButton.contains(e.target) && !menuDropdown.contains(e.target)) {
            menuDropdown.classList.remove('show');
          }
        });
      }

      if (clearButton) {
        clearButton.addEventListener('click', () => {
          if (confirm('Clear chat history? This cannot be undone.')) {
            this.clearChat();
            menuDropdown?.classList.remove('show');
          }
        });
      }
    }

    async sendMessage() {
      const message = this.elements.chatInput.value.trim();
      if (!message) return;

      const isFirstMessage = this.elements.chatMessages.querySelectorAll('.user-message').length === 0;
      
      this.addMessage(message, true);
      this.elements.chatInput.value = '';
      this.elements.sendButton.disabled = true;
      
      if (isFirstMessage) {
        this.heightManager.expandChatWindow();
      }
      
      const typingIndicator = this.showTypingIndicator();
      
      try {
        if (this.requestController) {
          this.requestController.abort();
        }
        
        const response = await this.sendToChatbase(message);
        this.hideTypingIndicator(typingIndicator);
        
        const hasProducts = response.products && response.products.length > 0;
        const aiMessageElement = this.addMessage(response.text, false, true, hasProducts);
        
        if (hasProducts) {
          setTimeout(() => this.addProductRecommendationsToMessage(response.products, aiMessageElement), CONFIG.ui.typingDelay);
        }
        
      } catch (error) {
        this.hideTypingIndicator(typingIndicator);
        if (error.name !== 'AbortError') {
          this.addMessage('Sorry, I encountered an error. Please try again.');
          Debug.log('normal', 'Error in sendMessage:', error);
        }
      }
      
      this.elements.sendButton.disabled = false;
    }

    async sendToChatbase(message) {
      try {
        Debug.log('verbose', '🚀 Starting Chatbase request...');
        Debug.log('verbose', '📝 Message:', message);
        Debug.log('verbose', '🔑 API Key:', CONFIG.chatbase.apiKey ? 'Present' : 'Missing');
        Debug.log('verbose', '🤖 Chatbot ID:', CONFIG.chatbase.chatbotId);
        Debug.log('verbose', '🌐 API URL:', CONFIG.chatbase.apiUrl);
        
        this.requestController = new AbortController();
        
        const messages = this.conversationHistory
          .filter(msg => msg.content && msg.content.trim() !== '')
          .slice(-CONFIG.storage.maxHistoryItems)
          .map(msg => ({
            role: msg.role === 'user' ? 'user' : 'assistant',
            content: msg.content
          }));
        
        messages.push({ role: 'user', content: message });
        
        Debug.log('verbose', '💬 Conversation history length:', messages.length);
        Debug.log('verbose', '📋 Messages payload:', JSON.stringify(messages, null, 2));
        
        const requestPayload = {
          chatbotId: CONFIG.chatbase.chatbotId,
          messages: messages
        };
        
        Debug.log('verbose', '📤 Full request payload:', JSON.stringify(requestPayload, null, 2));
        
        const requestOptions = {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${CONFIG.chatbase.apiKey}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json',
          },
          body: JSON.stringify(requestPayload),
          signal: this.requestController.signal
        };
        
        Debug.log('verbose', '🔧 Request headers:', requestOptions.headers);
        
        const response = await fetch(CONFIG.chatbase.apiUrl, requestOptions);
        
        Debug.log('normal', '📥 Response status:', response.status);
        Debug.log('verbose', '📥 Response ok:', response.ok);
        Debug.log('verbose', '📥 Response headers:', Object.fromEntries(response.headers.entries()));
        
        if (!response.ok) {
          let errorText = '';
          try {
            errorText = await response.text();
            Debug.log('normal', '❌ Error response body:', errorText);
          } catch (e) {
            Debug.log('normal', '❌ Could not read error response body:', e);
          }
          
          throw new Error(`Chatbase API error: ${response.status} - ${errorText}`);
        }
        
        const responseText = await response.text();
        Debug.log('verbose', '📄 Raw response:', responseText);
        
        let data;
        try {
          data = JSON.parse(responseText);
          Debug.log('verbose', '✅ Parsed response data:', data);
        } catch (parseError) {
          Debug.log('normal', '❌ Failed to parse JSON response:', parseError);
          Debug.log('normal', '❌ Response text:', responseText);
          throw new Error('Invalid JSON response from API');
        }
        
        return {
          text: data.text || 'Sorry, I encountered an error. Please try again.',
          products: this.extractProductNames(data.text || '')
        };
        
      } catch (error) {
        if (error.name === 'AbortError') {
          Debug.log('verbose', '⏹️ Request was aborted');
          throw error;
        }
        
        Debug.log('normal', '❌ Detailed error information:');
        Debug.log('normal', 'Error name:', error.name);
        Debug.log('normal', 'Error message:', error.message);
        Debug.log('verbose', 'Error stack:', error.stack);
        
        // Check for specific error types
        if (error instanceof TypeError && error.message.includes('fetch')) {
          Debug.log('normal', '🌐 Network error - possible CORS or connectivity issue');
        } else if (error.message.includes('401')) {
          Debug.log('normal', '🔑 Authentication error - check API key');
        } else if (error.message.includes('403')) {
          Debug.log('normal', '🚫 Authorization error - check permissions');
        } else if (error.message.includes('429')) {
          Debug.log('normal', '⏱️ Rate limit error - too many requests');
        } else if (error.message.includes('500')) {
          Debug.log('normal', '🔥 Server error - API issue');
        }
        
        return {
          text: 'Sorry, I\'m having trouble connecting right now. Please try again in a moment.',
          products: []
        };
      } finally {
        this.requestController = null;
      }
    }

    extractProductNames(text) {
      const products = [];
      const productPatterns = [
        /KIOXIA\s+[A-Z0-9-]+(?:\s+\([^)]+\))?(?:\s+-\s+[0-9.]+TB)?/gi,
        /iPhone\s+[0-9]+(?:\s+Pro(?:\s+Max)?)?/gi,
        /MacBook\s+(?:Pro|Air)(?:\s+[0-9]+(?:-inch)?)?/gi,
        /iPad\s+(?:Pro|Air|Mini)?(?:\s+[0-9]+(?:-inch)?)?/gi,
        /Apple\s+Watch\s+(?:Series\s+[0-9]+|Ultra|SE)/gi,
        /AirPods\s+(?:Pro|Max|[0-9]+(?:nd|rd|th)?(?:\s+Generation)?)?/gi
      ];
      
      productPatterns.forEach(pattern => {
        const matches = text.match(pattern);
        if (matches) {
          matches.forEach(match => {
            const cleanMatch = match.trim();
            if (cleanMatch && cleanMatch.length < 100 && !products.includes(cleanMatch)) {
              products.push(cleanMatch);
            }
          });
        }
      });
      
      if (products.length > 0) {
        Debug.log('normal', '🛍️ PRODUCTS DETECTED:', products);
      }
      
      return products;
    }

    addMessage(content, isUser = false, shouldSave = true, hasProducts = false) {
      const messageDiv = document.createElement('div');
      messageDiv.className = `message ${isUser ? 'user-message' : 'ai-message-bubble'}`;
      
      if (!isUser && hasProducts) {
        messageDiv.classList.add('has-products');
      }
      
      const contentDiv = document.createElement('div');
      contentDiv.className = 'message-content';
      
      const formattedContent = content
        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
        .replace(/\n/g, '<br>')
        .replace(/(\d+\.\s)/g, '<br>$1');
      
      contentDiv.innerHTML = `<p>${formattedContent}</p>`;
      messageDiv.appendChild(contentDiv);
      this.elements.chatMessages.appendChild(messageDiv);
      
      if (isUser) {
        this.scrollToBottom();
      } else {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => this.scrollToTopOfMessage(messageDiv));
        });
      }
      
      if (shouldSave) {
        this.conversationHistory.push({
          role: isUser ? 'user' : 'assistant',
          content: content,
          timestamp: Date.now()
        });
        this.saveConversation();
      }
      
      return messageDiv;
    }

    showTypingIndicator() {
      const typingDiv = document.createElement('div');
      typingDiv.className = 'typing-indicator';
      typingDiv.innerHTML = `
        <div class="typing-dots">
          <div class="typing-dot"></div>
          <div class="typing-dot"></div>
          <div class="typing-dot"></div>
        </div>
      `;
      
      this.elements.chatMessages.appendChild(typingDiv);
      this.scrollToBottom();
      return typingDiv;
    }

    hideTypingIndicator(typingIndicator) {
      if (typingIndicator && typingIndicator.parentNode) {
        typingIndicator.remove();
      }
    }

    scrollToBottom() {
      this.elements.chatMessages.scrollTop = this.elements.chatMessages.scrollHeight;
    }

    scrollToTopOfMessage(messageElement) {
      messageElement.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
        inline: 'nearest'
      });
    }

    addProductRecommendationsToMessage(products, messageElement, shouldSave = true) {
      const productsDiv = document.createElement('div');
      productsDiv.className = 'product-recommendations';
      
      products.forEach(productName => {
        const productCard = document.createElement('div');
        productCard.className = 'product-card';
        productCard.innerHTML = `
          <div class="product-image" style="background: linear-gradient(135deg, #f3f4f6, #e5e7eb);"></div>
          <div class="product-info">
            <div class="product-title">${productName}</div>
            <div class="product-price">Loading...</div>
          </div>
        `;
        
        productsDiv.appendChild(productCard);
        this.searchShopifyProduct(productName, productCard);
      });
      
      // Insert the products div right after the message element
      messageElement.parentNode.insertBefore(productsDiv, messageElement.nextSibling);
      
      setTimeout(() => this.scrollToTopOfMessage(productsDiv), 100);
      
      if (shouldSave && this.conversationHistory.length > 0) {
        const lastMessage = this.conversationHistory[this.conversationHistory.length - 1];
        if (lastMessage.role === 'assistant') {
          lastMessage.products = products;
          this.saveConversation();
        }
      }
    }

    addProductRecommendations(products, shouldSave = true) {
      const productsDiv = document.createElement('div');
      productsDiv.className = 'product-recommendations';
      
      products.forEach(productName => {
        const productCard = document.createElement('div');
        productCard.className = 'product-card';
        productCard.innerHTML = `
          <div class="product-image" style="background: linear-gradient(135deg, #f3f4f6, #e5e7eb);"></div>
          <div class="product-info">
            <div class="product-title">${productName}</div>
            <div class="product-price">Loading...</div>
          </div>
        `;
        
        productsDiv.appendChild(productCard);
        this.searchShopifyProduct(productName, productCard);
      });
      
      // Append to the end of chat messages (for new messages)
      this.elements.chatMessages.appendChild(productsDiv);
      
      setTimeout(() => this.scrollToTopOfMessage(productsDiv), 100);
      
      if (shouldSave && this.conversationHistory.length > 0) {
        const lastMessage = this.conversationHistory[this.conversationHistory.length - 1];
        if (lastMessage.role === 'assistant') {
          lastMessage.products = products;
          this.saveConversation();
        }
      }
    }

    async searchShopifyProduct(productName, productCard) {
      try {
        const query = `
          query searchProducts($query: String!, $first: Int!) {
            search(query: $query, first: $first, types: PRODUCT) {
              edges {
                node {
                  ... on Product {
                    id
                    title
                    handle
                    images(first: 1) {
                      edges {
                        node {
                          url
                          altText
                        }
                      }
                    }
                    priceRange {
                      minVariantPrice {
                        amount
                        currencyCode
                      }
                    }
                  }
                }
              }
            }
          }
        `;
        
        const response = await fetch(`https://${CONFIG.shopify.storeDomain}/api/${CONFIG.shopify.apiVersion}/graphql.json`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Shopify-Storefront-Access-Token': CONFIG.shopify.storefrontToken
          },
          body: JSON.stringify({
            query: query,
            variables: { query: productName, first: 1 }
          })
        });
        
        if (!response.ok) throw new Error(`Shopify API error: ${response.status}`);
        
        const data = await response.json();
        if (data.errors) throw new Error('GraphQL errors: ' + JSON.stringify(data.errors));
        
        const products = data.data?.search?.edges || [];
        
        if (products.length > 0) {
          const product = products[0].node;
          const image = product.images?.edges?.[0]?.node;
          const price = product.priceRange?.minVariantPrice;
          
          this.updateProductCard(productCard, {
            title: product.title,
            price: price ? `£${parseFloat(price.amount).toFixed(2)}` : 'Price unavailable',
            url: `https://www.bytehaus.co.uk/products/${product.handle}`,
            image: image?.url || null
          });
        } else {
          this.updateProductCard(productCard, {
            title: productName,
            price: 'Not found - Search all products',
            url: `https://www.bytehaus.co.uk/search?q=${encodeURIComponent(productName)}`,
            image: null
          });
        }
        
      } catch (error) {
        Debug.log('normal', 'Error searching Shopify API:', error);
        this.updateProductCard(productCard, {
          title: productName,
          price: 'Search Products',
          url: `https://www.bytehaus.co.uk/search?q=${encodeURIComponent(productName)}`,
          image: null
        });
      }
    }

    updateProductCard(productCard, productData) {
      const titleElement = productCard.querySelector('.product-title');
      const priceElement = productCard.querySelector('.product-price');
      const imageElement = productCard.querySelector('.product-image');
      
      if (titleElement && productData.title) {
        titleElement.textContent = productData.title;
      }
      
      if (priceElement) {
        priceElement.textContent = productData.price || 'View product';
      }
      
      if (imageElement && productData.image) {
        imageElement.style.background = 'none';
        imageElement.innerHTML = `<img src="${productData.image}" alt="${productData.title}" style="width: 100%; height: 100%; object-fit: cover; border-radius: 8px;">`;
      }
      
      if (productData.url) {
        productCard.onclick = () => window.open(productData.url, '_blank');
        productCard.style.cursor = 'pointer';
      }
    }

    loadConversation() {
      try {
        const saved = localStorage.getItem(CONFIG.storage.conversationKey);
        if (saved) {
          this.conversationHistory = JSON.parse(saved);
          
          if (this.conversationHistory.length > 0) {
            const welcomeMessage = this.elements.chatMessages.querySelector('.message');
            if (welcomeMessage) welcomeMessage.remove();
          }
          
          // Process messages sequentially to maintain proper order
          this.conversationHistory.forEach((msg, index) => {
            if (msg.role === 'user') {
              this.addMessage(msg.content, true, false);
            } else {
              const hasProducts = msg.products && msg.products.length > 0;
              const messageElement = this.addMessage(msg.content, false, false, hasProducts);
              
              // Add products immediately after the message, not with setTimeout
              if (hasProducts) {
                this.addProductRecommendationsToMessage(msg.products, messageElement, false);
              }
            }
          });
          
          if (this.conversationHistory.some(msg => msg.role === 'user')) {
            this.heightManager.expandChatWindow();
          }
        }
      } catch (error) {
        Debug.log('normal', 'Error loading conversation:', error);
      }
    }

    saveConversation() {
      try {
        const recentHistory = this.conversationHistory.slice(-CONFIG.storage.maxHistoryItems);
        localStorage.setItem(CONFIG.storage.conversationKey, JSON.stringify(recentHistory));
      } catch (error) {
        Debug.log('normal', 'Error saving conversation:', error);
      }
    }

    clearChat() {
      this.elements.chatMessages.innerHTML = `
        <div class="message ai-message-bubble">
          <div class="message-content">
            <p>Ask me anything from product SKU's to advice about what is suitable for your business.</p>
          </div>
        </div>
      `;
      
      this.conversationHistory = [];
      this.saveConversation();
      
      this.core.removeAttribute('data-chat-expanded');
      this.core.removeAttribute('data-should-expand-chat');
      this.core.removeAttribute('data-mobile-expanded');
      this.core.removeAttribute('data-mobile-expansion-locked');
      
      this.heightManager.resetAllHeights();
    }
  }

  // ==================== TAB MANAGEMENT ====================
  class TabManager {
    constructor(core) {
      this.core = core;
      this.heightManager = new HeightManager(core);
      this.tabs = core.querySelectorAll('.tab');
      this.contentAreas = core.querySelectorAll('.tab-content');
      
      this.bindEvents();
    }

    bindEvents() {
      this.tabs.forEach(tab => {
        tab.addEventListener('click', (e) => this.handleTabClick(e, tab));
      });
    }

    handleTabClick(e, tab) {
      e.preventDefault();
      
      const targetTab = tab.getAttribute('data-tab');
      const currentActiveTab = this.core.querySelector('.tab.active');
      const previousTab = currentActiveTab?.getAttribute('data-tab') || null;
      
      this.handleTabTransition(previousTab, targetTab);
      this.updateTabStates(tab, targetTab);
      this.handlePostSwitchActions(targetTab);
    }

    handleTabTransition(previousTab, targetTab) {
      const { isMobile } = state.getViewportConstraints();
      
      if (isMobile && state.shouldMaintainMobileExpansion(this.core)) {
        if (previousTab === 'ai') {
          const hasUserMessages = this.core.querySelector('#chatMessages .user-message');
          const chatMessagesCount = this.core.querySelectorAll('#chatMessages .message').length;
          const hasActualMessages = hasUserMessages || chatMessagesCount > 1;
          
          if (hasActualMessages) {
            this.core.setAttribute('data-should-expand-chat', 'true');
          }
        }
        return;
      }

      if (previousTab === 'ai') {
        const hasUserMessages = this.core.querySelector('#chatMessages .user-message');
        const chatMessagesCount = this.core.querySelectorAll('#chatMessages .message').length;
        const hasActualMessages = hasUserMessages || chatMessagesCount > 1;
        
        if (hasActualMessages) {
          this.core.setAttribute('data-should-expand-chat', 'true');
        } else {
          this.core.removeAttribute('data-should-expand-chat');
        }
        
        this.heightManager.resetAllHeights();
        this.core.removeAttribute('data-chat-expanded');
      } else if (targetTab === 'ai') {
        this.heightManager.resetAllHeights();
      }
    }

    updateTabStates(activeTab, targetTab) {
      this.tabs.forEach(t => t.classList.remove('active'));
      activeTab.classList.add('active');
      
      this.contentAreas.forEach(content => {
        content.classList.remove('active');
        content.style.display = 'none';
      });
      
      const targetContent = this.core.querySelector(`[data-content="${targetTab}"]`);
      if (targetContent) {
        targetContent.classList.add('active');
        targetContent.style.display = 'flex';
        
        this.handleMobileExpandedContent(targetContent, targetTab);
      }
      
      const skeletonContent = this.core.querySelector('.skeleton-content');
      if (skeletonContent) {
        skeletonContent.style.display = 'none';
      }
    }

    handleMobileExpandedContent(targetContent, targetTab) {
      const constraints = state.getViewportConstraints();
      if (constraints.isMobile && this.core.hasAttribute('data-mobile-expanded')) {
        Utils.addMobileTransitions(targetContent);
        Utils.addMobileTransitions(this.core);
        
        this.core.style.height = '100%';
        this.core.style.display = 'flex';
        this.core.style.flexDirection = 'column';
        
        targetContent.classList.add('mobile-expanded-content');
        targetContent.style.height = '100%';
        targetContent.style.flex = '1';
        targetContent.style.display = 'flex';
        targetContent.style.flexDirection = 'column';
        targetContent.style.minHeight = '0';
        
        if (targetTab === 'ai') {
          this.heightManager.configureChatForMobile(targetContent);
        }
      }
    }

    handlePostSwitchActions(targetTab) {
      const { isMobile } = state.getViewportConstraints();
      
      if (targetTab === 'ai') {
        setTimeout(() => {
          const targetContent = this.core.querySelector(`[data-content="${targetTab}"]`);
          const chatInput = targetContent?.querySelector('#chatInput');
          if (chatInput) {
            if (isMobile) {
              chatInput.click();
              chatInput.focus();
            } else {
              chatInput.focus();
            }
          }
          
          const shouldExpandFromState = this.core.hasAttribute('data-should-expand-chat');
          const hasUserMessages = this.core.querySelector('#chatMessages .user-message');
          const chatMessagesCount = this.core.querySelectorAll('#chatMessages .message').length;
          
          if (shouldExpandFromState || hasUserMessages || chatMessagesCount > 1) {
            this.heightManager.expandChatWindow();
            this.core.removeAttribute('data-should-expand-chat');
          } else {
            this.core.removeAttribute('data-should-expand-chat');
          }
        }, 100);
        
      } else if (targetTab === 'products') {
        setTimeout(() => {
          const targetContent = this.core.querySelector(`[data-content="${targetTab}"]`);
          const searchInput = targetContent?.querySelector('#Search-In-Hybrid-Products');
          if (searchInput) {
            if (isMobile) {
              searchInput.click();
              searchInput.focus();
              
              setTimeout(() => {
                searchInput.focus();
                const clickEvent = new Event('click', { bubbles: true });
                searchInput.dispatchEvent(clickEvent);
                
                setTimeout(() => {
                  state.updateViewportHeight();
                  if (state.shouldMaintainMobileExpansion(this.core)) {
                    this.heightManager.expandForMobile();
                  }
                }, 400);
              }, 100);
            } else {
              searchInput.focus();
            }
          }
          
          if (!isMobile || state.shouldMaintainMobileExpansion(this.core)) {
            this.heightManager.adjustForProductSearch();
          }
        }, 100);
      }
    }
  }

  // ==================== SEARCH FUNCTIONALITY ====================
  class SearchManager {
    constructor(core) {
      this.core = core;
      this.heightManager = new HeightManager(core);
      this.setupObservers();
      this.bindSearchEvents();
    }

    setupObservers() {
      const predictiveSearch = this.core.querySelector('.predictive-search');
      if (!predictiveSearch) return;
      
      const predictiveSearchElement = this.core.closest('predictive-search');
      if (predictiveSearchElement && predictiveSearchElement.calcHeight) {
        predictiveSearchElement.calcHeight = () => {
          if (!state.isAdjusting) {
            this.heightManager.adjustForProductSearch();
          }
        };
      }
      
      const debouncedAdjust = Utils.debounce(() => {
        this.heightManager.adjustForProductSearch();
      }, CONFIG.ui.debounceDelay);
      
      const observer = new MutationObserver((mutations) => {
        if (!state.isAdjusting) debouncedAdjust();
      });
      
      observer.observe(predictiveSearch, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class', 'style']
      });
      
      const resultsContainer = this.core.querySelector('#predictive-search-results');
      if (resultsContainer) {
        const resultsObserver = new MutationObserver(() => {
          if (!state.isAdjusting) debouncedAdjust();
        });
        
        resultsObserver.observe(resultsContainer, {
          childList: true,
          subtree: true,
          attributes: true
        });
      }
    }

    bindSearchEvents() {
      const searchInput = this.core.querySelector('#Search-In-Hybrid-Products');
      if (!searchInput) return;
      
      const debouncedInputHandler = Utils.debounce(() => {
        const { isMobile } = state.getViewportConstraints();
        if (!isMobile || state.shouldMaintainMobileExpansion(this.core) || state.hasExpandableContent(this.core)) {
          this.heightManager.adjustForProductSearch();
        }
      }, CONFIG.ui.debounceDelay);
      
      searchInput.addEventListener('input', () => {
        if (!state.isAdjusting) debouncedInputHandler();
      });
      
      searchInput.addEventListener('focus', () => {
        const { isMobile } = state.getViewportConstraints();
        if (isMobile) {
          setTimeout(() => {
            state.updateViewportHeight();
            if (!isMobile || state.shouldMaintainMobileExpansion(this.core)) {
              this.heightManager.adjustForProductSearch();
            }
          }, CONFIG.ui.typingDelay);
        } else {
          setTimeout(() => {
            this.heightManager.adjustForProductSearch();
          }, CONFIG.ui.typingDelay);
        }
      });
      
      searchInput.addEventListener('blur', () => {
        const { isMobile } = state.getViewportConstraints();
        if (isMobile) {
          setTimeout(() => {
            state.updateViewportHeight();
            this.heightManager.adjustForProductSearch();
          }, CONFIG.ui.typingDelay);
        } else {
          setTimeout(() => {
            this.heightManager.adjustForProductSearch();
          }, CONFIG.ui.typingDelay);
        }
      });
    }
  }

  // ==================== MAIN INITIALIZATION ====================
  function initializeHybridSearchCore(core) {
    if (core.hasAttribute('data-initialized')) return;
    core.setAttribute('data-initialized', 'true');
    
    Debug.log('verbose', '🚀 Initializing hybrid search core...');
    
    // Run initial configuration validation
    Debug.validateConfig();
    
    const tabManager = new TabManager(core);
    const searchManager = new SearchManager(core);
    const chatInterface = new ChatInterface(core);
    
    // Initialize the product search disabler
    const productSearchDisabler = new ProductSearchDisabler(core);
    
    state.cores.set(core, {
      tabManager,
      searchManager,
      chatInterface,
      heightManager: new HeightManager(core),
      productSearchDisabler
    });
    
    const activeTab = core.querySelector('.tab.active');
    if (activeTab) {
      const initialTab = activeTab.getAttribute('data-tab');
      const initialContent = core.querySelector(`[data-content="${initialTab}"]`);
      if (initialContent) {
        initialContent.style.display = 'flex';
        
        const { isMobile } = state.getViewportConstraints();
        const focusDelay = isMobile ? 300 : 150;
        
        setTimeout(() => {
          if (initialTab === 'ai') {
            const chatInput = initialContent.querySelector('#chatInput');
            if (chatInput) {
              if (isMobile) {
                chatInput.click();
                chatInput.focus();
              } else {
                chatInput.focus();
              }
            }
          } else if (initialTab === 'products') {
            const searchInput = initialContent.querySelector('#Search-In-Hybrid-Products');
            if (searchInput) {
              if (isMobile) {
                searchInput.click();
                searchInput.focus();
                
                setTimeout(() => {
                  searchInput.focus();
                  const clickEvent = new Event('click', { bubbles: true });
                  searchInput.dispatchEvent(clickEvent);
                  
                  setTimeout(() => {
                    state.updateViewportHeight();
                  }, 400);
                }, 100);
              } else {
                searchInput.focus();
              }
            }
          }
        }, focusDelay);
      }
    }
    
    Debug.log('normal', '✅ Hybrid search core initialized successfully');
  }

  function initializeAllHybridSearchTabs() {
    Debug.log('normal', '🔧 Initializing all hybrid search tabs...');
    const searchCores = document.querySelectorAll('.hybrid-search-core');
    searchCores.forEach(initializeHybridSearchCore);
    Debug.log('normal', `✅ Initialized ${searchCores.length} hybrid search cores`);
  }

  function setupGlobalEventListeners() {
    Debug.log('verbose', '🌐 Setting up global event listeners...');
    
    state.updateCSSViewportHeight();
    
    const isIOSSafari = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    const isMobile = window.innerWidth <= CONFIG.ui.mobileBreakpoint;
    
    if (isIOSSafari || isMobile) {
      let initialViewportHeight = window.innerHeight;
      let keyboardOpen = false;
      
      const forceJSHeight = (isKeyboardEvent = false) => {
        const actualHeight = window.innerHeight;
        document.documentElement.style.setProperty('--actual-vh', `${actualHeight}px`);
        
        state.actualViewportHeight = actualHeight;
        
        state.cores.forEach((managers, core) => {
          if (core.hasAttribute('data-mobile-expanded')) {
            const mobileDrawerContent = core.closest('.mobile-drawer-content');
            if (mobileDrawerContent && !state.isProblematicWebview) {
              mobileDrawerContent.style.height = `${actualHeight}px`;
              mobileDrawerContent.style.maxHeight = `${actualHeight}px`;
            }
          }
        });
        
        if (isKeyboardEvent) {
          state.cores.forEach((managers, core) => {
            managers.heightManager.updateConstraints();
            if (state.shouldMaintainMobileExpansion(core)) {
              managers.heightManager.expandForMobile();
            }
          });
        }
      };
      
      const handleViewportChange = () => {
        const currentHeight = window.innerHeight;
        const heightDifference = initialViewportHeight - currentHeight;
        
        if (heightDifference > 100) {
          if (!keyboardOpen) {
            keyboardOpen = true;
            forceJSHeight(true);
          }
        } else if (heightDifference < 50) {
          if (keyboardOpen) {
            keyboardOpen = false;
            initialViewportHeight = currentHeight;
            forceJSHeight(true);
          }
        }
      };
      
      window.addEventListener('resize', Utils.throttle(() => {
        handleViewportChange();
        forceJSHeight();
      }, 50));
      
      window.addEventListener('orientationchange', () => {
        setTimeout(() => {
          initialViewportHeight = window.innerHeight;
          forceJSHeight(true);
        }, 300);
      });
      
      if (window.visualViewport) {
        window.visualViewport.addEventListener('resize', () => {
          const currentHeight = window.visualViewport.height;
          document.documentElement.style.setProperty('--actual-vh', `${currentHeight}px`);
          state.actualViewportHeight = currentHeight;
          
          state.cores.forEach((managers, core) => {
            if (core.hasAttribute('data-mobile-expanded')) {
              const mobileDrawerContent = core.closest('.mobile-drawer-content');
              if (mobileDrawerContent && !state.isProblematicWebview) {
                mobileDrawerContent.style.height = `${currentHeight}px`;
                mobileDrawerContent.style.maxHeight = `${currentHeight}px`;
              }
              managers.heightManager.updateConstraints();
            }
          });
        });
      }
      
      document.addEventListener('focusin', (e) => {
        if (e.target.matches('input, textarea')) {
          setTimeout(() => {
            handleViewportChange();
            forceJSHeight(true);
          }, 300);
        }
      });
      
      document.addEventListener('focusout', (e) => {
        if (e.target.matches('input, textarea')) {
          setTimeout(() => {
            handleViewportChange();
            forceJSHeight(true);
          }, 300);
        }
      });
      
      setTimeout(() => {
        initialViewportHeight = window.innerHeight;
        forceJSHeight();
      }, 100);
      
      let isScrolling = false;
      window.addEventListener('scroll', Utils.throttle(() => {
        if (!isScrolling) {
          isScrolling = true;
          setTimeout(() => {
            forceJSHeight();
            isScrolling = false;
          }, 150);
        }
      }, 100));
    }
    
    const throttledResize = Utils.throttle(() => {
      state.updateViewportHeight();
      state.cores.forEach((managers, core) => {
        managers.heightManager.updateConstraints();
        managers.heightManager.adjustForProductSearch();
      });
    }, 150);
    
    window.addEventListener('resize', throttledResize);
    
    window.addEventListener('orientationchange', () => {
      setTimeout(() => {
        state.updateViewportHeight();
        state.cores.forEach((managers, core) => {
          managers.heightManager.updateConstraints();
          managers.heightManager.adjustForProductSearch();
        });
      }, 500);
    });
    
    let lastViewportHeight = window.innerHeight;
    const checkViewportHeight = Utils.throttle(() => {
      const currentHeight = window.innerHeight;
      if (Math.abs(currentHeight - lastViewportHeight) > 10) {
        lastViewportHeight = currentHeight;
        state.updateViewportHeight();
        
        state.cores.forEach((managers, core) => {
          managers.heightManager.updateConstraints();
          
          if (managers.heightManager.constraints.isMobile && 
              state.shouldMaintainMobileExpansion(core)) {
            managers.heightManager.expandForMobile();
          } else {
            managers.heightManager.adjustForProductSearch();
          }
        });
      }
    }, 100);
    
    setInterval(checkViewportHeight, 300);
    
    Debug.log('verbose', '✅ Global event listeners setup complete');
  }

  // ==================== INITIALIZATION ====================
  function initialize() {
    Debug.log('normal', '🚀 Starting Hybrid Search initialization...');
    
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        initializeAllHybridSearchTabs();
        setupGlobalEventListeners();
      });
    } else {
      initializeAllHybridSearchTabs();
      setupGlobalEventListeners();
    }
    
    Debug.log('normal', '✅ Hybrid Search initialization complete');
  }

  // Global access
  window.initializeHybridSearchTabs = initializeAllHybridSearchTabs;

  // Auto-initialize
  initialize();

})();

console.log("Enhanced Hybrid Search v8 with disabled product search submission loaded");

// Global debugging functions for console access
window.debugChatbase = async function() {
  console.log('🔍 Running Chatbase diagnostics...');
  await window.HybridSearchDebug.validateConfig();
  await window.HybridSearchDebug.checkCORS();
  await window.HybridSearchDebug.testChatbaseAPI();
};